import axios from 'axios';
import { adoptUser, setCurrentUserId } from './draftStorage';
import {
  broadcastSessionEvent,
  CSRF_COOKIE_NAME,
  endSession,
  markSession,
  notifySignedIn,
  readCookie,
} from './session';

// Re-exported for callers that already import the session hint from here.
export { hasSession } from './session';

const API_URL = '';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

// Current UI language ('en'/'fr'). Mirrors getLang() in LanguageToggle.
// read directly from localStorage to avoid a service→component import. Sent
// with chat messages so the backend can localize canned early-exit replies
// (greeting / out-of-scope) that bypass the language-mirroring LLM.
function uiLanguage() {
  try {
    const stored = localStorage.getItem('prism_language');
    if (stored === 'en' || stored === 'fr') return stored;
    return (navigator.language || '').startsWith('fr') ? 'fr' : 'en';
  } catch {
    return 'en';
  }
}

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      const csrf = readCookie(CSRF_COOKIE_NAME);
      if (csrf) config.headers[CSRF_HEADER_NAME] = csrf;
    }
    config.headers['Cache-Control'] = 'no-cache';
    if (method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Not every 403 is session-fatal: an admin-only endpoint refusing a regular
// user is a legitimate answer the caller should render. These two are fatal —
// the credential itself is no longer usable, because the CSRF token drifted
// out of sync with a rotated cookie or the account was deactivated mid-session
// — and a page left sitting on top of one is dead in the water.
const FATAL_403_DETAILS = ['CSRF check failed', 'User account is inactive'];

function isFatal403(detail) {
  return typeof detail === 'string'
    && FATAL_403_DETAILS.some((known) => detail.includes(known));
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401 || (status === 403 && isFatal403(error.response?.data?.detail))) {
      markSession(false);
      // IdleTimeout's session probes pass _skipAuthRedirect so the component
      // controls the redirect timing itself (avoids the modal flashing for
      // one render before endSession's navigation kicks in).
      if (!error.config?._skipAuthRedirect) endSession();
    }
    return Promise.reject(error);
  },
);

// Every raw fetch() in this file bypasses the axios interceptor above, so each
// one routes its response through here. Without it, a session that expired
// mid-stream or mid-upload surfaced as a "Server error 401" message on a page
// that stayed up and unusable until the clinician thought to reload.
async function guardFetchResponse(res) {
  if (res.ok || (res.status !== 401 && res.status !== 403)) return res;
  let detail = '';
  try {
    // clone() so the caller can still read the body for its own error message.
    const body = await res.clone().json();
    detail = body?.detail || '';
  } catch {
    /* non-JSON error body */
  }
  if (res.status === 401 || isFatal403(detail)) {
    markSession(false);
    endSession();
  }
  return res;
}

function fetchHeaders(extra = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    ...extra,
  };
  const csrf = readCookie(CSRF_COOKIE_NAME);
  if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  return headers;
}

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    notifySignedIn();
    // Per-user namespacing for draft storage: subsequent reads / writes land
    // under prism_drafts:<user_id>. adoptUser() also runs the one-time legacy
    // migration (no-op once the pre-namespacing keys are gone).
    adoptUser(response?.data?.user?.id);
    return response.data;
  },

  // Exchange a Google ID token for a PRISM session. The backend verifies the
  // signature, enforces the @telusmd.ca hosted domain, and JIT-provisions the
  // user on first login. `nonce` is the same random string passed to GIS in
  // the browser, echoed back here so the backend can match it.
  loginWithGoogle: async (credential, nonce = null) => {
    const response = await api.post('/api/auth/google', { credential, nonce });
    notifySignedIn();
    adoptUser(response?.data?.user?.id);
    return response.data;
  },

  logout: async () => {
    try {
      const response = await api.post('/api/auth/logout');
      return response.data;
    } finally {
      markSession(false);
      // Tell sibling tabs the cookie is gone. Without this a second PRISM tab
      // keeps rendering chat over a session that no longer exists until its
      // own heartbeat catches up (or the clinician reloads).
      broadcastSessionEvent('logout');
      // Clear the current-user pointer so any further draft writes between
      // logout and the next login become no-ops (we can't safely attribute
      // them to a user). The actual drafts stay in localStorage under
      // prism_drafts:<previous_user_id> and reappear when that user signs
      // back in.
      setCurrentUserId(null);
    }
  },

  getCurrentUser: async (config = {}) => {
    const response = await api.get('/api/auth/me', config);
    // Claim the draft namespace here too, not just in the login handlers
    // above. TELUS PingID signs the user in with a server-side 302 that runs
    // no JS at all, so /me is the first — and for that flow the only — moment
    // the SPA learns who it is talking to. Without this, prism_current_user_id
    // is never set for an SSO session and every draft read/write no-ops, which
    // is why the restore banner never appeared in production.
    adoptUser(response?.data?.id);
    return response.data;
  },
};

// Public, unauthenticated runtime configuration. The Login page fetches this
// once on mount to learn the Google OAuth Client ID — which is loaded by the
// backend from Google Secret Manager at startup, the same way the backend's
// other secrets are loaded. This lets the SPA pick up Client-ID changes via
// a simple backend restart, with no frontend rebuild.
export const configAPI = {
  getPublicConfig: async () => {
    const response = await api.get('/api/config/public');
    return response.data;
  },
};

export const conversationsAPI = {
  list: async () => {
    const response = await api.get('/api/conversations');
    return response.data;
  },

  create: async (title, initialMessage = null, tempNamespace = null, attachmentFilename = null) => {
    const response = await api.post('/api/conversations', {
      title,
      initial_message: initialMessage,
      temp_namespace: tempNamespace,
      attachment_filename: attachmentFilename,
    });
    return response.data;
  },

  initConversation: async (initialMessage = null, title = null, tempNamespace = null, attachmentFilename = null) => {
    const response = await api.post('/api/conversations/init', {
      title: title || null,
      initial_message: initialMessage || null,
      temp_namespace: tempNamespace || null,
      attachment_filename: attachmentFilename || null,
    });
    return response.data;
  },

  // Idempotent: asks the backend for this conversation's title, joining the
  // generation already in flight rather than starting another. Safe to poll.
  // Errors are reported as a status the caller can act on — a 404/403 means
  // stop asking, anything else means try again — instead of a bare null, which
  // is why a stranded "Generating title…" used to be unrecoverable.
  updateConversationTitle: async (conversationId, initialMessage) => {
    try {
      const response = await api.post(
        `/api/conversations/init/${conversationId}/update-title`,
        { initial_message: initialMessage || null, title: null },
      );
      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      console.warn('[title] update-title failed:', status || err?.message);
      return { title: null, errorStatus: status || 0 };
    }
  },

  get: async (conversationId) => {
    const response = await api.get(`/api/conversations/${conversationId}`);
    return response.data;
  },

  update: async (conversationId, title) => {
    const response = await api.put(`/api/conversations/${conversationId}`, { title });
    return response.data;
  },

  delete: async (conversationId) => {
    await api.delete(`/api/conversations/${conversationId}`);
  },

  detachAttachment: async (conversationId) => {
    await api.delete(`/api/conversations/${conversationId}/attachment`);
  },

  sendMessage: async (conversationId, content) => {
    const response = await api.post(
      `/api/conversations/${conversationId}/messages`,
      { content, language: uiLanguage() },
    );
    return response.data;
  },

  sendMessageStream: (conversationId, content, callbacks = {}, responseMode = 'detailed') => {
    const { onSources, onToken, onTrimTo, onComplete, onError, onUserMessage } = callbacks;
    const controller = new AbortController();
    // Exactly one terminal callback (onComplete or onError) must fire per
    // stream: the caller releases its "a stream is live" lock there, so a
    // stream that ended with neither would leave the composer disabled.
    let settled = false;

    const run = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/conversations/${conversationId}/messages/stream`,
          {
            method: 'POST',
            headers: fetchHeaders(),
            credentials: 'include',
            body: JSON.stringify({ content, response_mode: responseMode, language: uiLanguage() }),
            signal: controller.signal,
          },
        );

        await guardFetchResponse(res);
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          settled = true;
          onError && onError(`Server error ${res.status}: ${text}`);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice('data: '.length).trim();
            if (!jsonStr) continue;

            let event;
            try { event = JSON.parse(jsonStr); } catch { continue; }

            if (event.type === 'user_message') {
              // Backend echoes the DLP-masked version of the user's message so
              // the UI can replace the optimistic (raw) bubble immediately.
              onUserMessage && onUserMessage(event.content || '', event.has_pii, event.message_id);
            } else if (event.type === 'sources') {
              onSources && onSources(event.sources || [], event.source_type);
            } else if (event.type === 'sources_complete') {
              onSources && onSources(event.sources || [], null);
            } else if (event.type === 'trim_to') {
              onTrimTo && onTrimTo(event.length);
            } else if (event.type === 'token') {
              onToken && onToken(event.token || '');
            } else if (event.type === 'complete') {
              if (event.sources && event.sources.length > 0) {
                onSources && onSources(event.sources, null);
              }
              settled = true;
              onComplete && onComplete(event.message_id, event);
            } else if (event.type === 'error') {
              settled = true;
              onError && onError(event.error || 'Unknown streaming error');
            }
          }
        }

        // Reader drained with no `complete`/`error` event - a dropped
        // connection, a proxy timeout, or a backend restart part-way through.
        if (!settled) {
          settled = true;
          onError && onError('Stream ended unexpectedly');
        }
      } catch (err) {
        // An abort is deliberate teardown (unmount, or the conversation being
        // deleted) and the caller drops that state, so it stays quiet.
        if (err.name !== 'AbortError' && !settled) {
          settled = true;
          onError && onError(err.message || 'Stream connection failed');
        }
      }
    };

    run();
    return controller;
  },

  search: async (query) => {
    const response = await api.get(`/api/conversations/search/${encodeURIComponent(query)}`);
    return response.data;
  },

  getSignedUrl: async (filename) => {
    const response = await api.get(`/api/conversations/signed-url/${encodeURIComponent(filename)}`);
    return response.data;
  },

  sendMessageWithFile: async (conversationId, content, tempNamespace) => {
    const response = await api.post(
      `/api/conversations/${conversationId}/messages-with-file`,
      { content, temp_namespace: tempNamespace, language: uiLanguage() },
    );
    return response.data;
  },

  sendMessageWithFileStream: (
    conversationId,
    content,
    tempNamespace,
    filename = 'Attached Document',
    callbacks = {},
    responseMode = 'detailed',
  ) => {
    const { onSources, onToken, onTrimTo, onComplete, onError, onUserMessage } = callbacks;
    const controller = new AbortController();
    // Exactly one terminal callback (onComplete or onError) must fire per
    // stream: the caller releases its "a stream is live" lock there, so a
    // stream that ended with neither would leave the composer disabled.
    let settled = false;

    const run = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/conversations/${conversationId}/messages-with-file/stream`,
          {
            method: 'POST',
            headers: fetchHeaders(),
            credentials: 'include',
            body: JSON.stringify({
              content,
              response_mode: responseMode,
              temp_namespace: tempNamespace,
              filename,
              language: uiLanguage(),
            }),
            signal: controller.signal,
          },
        );

        await guardFetchResponse(res);
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          settled = true;
          onError && onError(`Server error ${res.status}: ${text}`);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice('data: '.length).trim();
            if (!jsonStr) continue;

            let event;
            try { event = JSON.parse(jsonStr); } catch { continue; }

            if (event.type === 'user_message') {
              // Backend echoes the DLP-masked version of the user's message so
              // the UI can replace the optimistic (raw) bubble immediately.
              onUserMessage && onUserMessage(event.content || '', event.has_pii, event.message_id);
            } else if (event.type === 'sources') {
              onSources && onSources(event.sources || [], event.source_type);
            } else if (event.type === 'sources_complete') {
              onSources && onSources(event.sources || [], null);
            } else if (event.type === 'trim_to') {
              onTrimTo && onTrimTo(event.length);
            } else if (event.type === 'token') {
              onToken && onToken(event.token || '');
            } else if (event.type === 'complete') {
              if (event.sources && event.sources.length > 0) {
                onSources && onSources(event.sources, null);
              }
              settled = true;
              onComplete && onComplete(event.message_id, event);
            } else if (event.type === 'error') {
              settled = true;
              onError && onError(event.error || 'Unknown streaming error');
            }
          }
        }

        // Reader drained with no `complete`/`error` event - a dropped
        // connection, a proxy timeout, or a backend restart part-way through.
        if (!settled) {
          settled = true;
          onError && onError('Stream ended unexpectedly');
        }
      } catch (err) {
        // An abort is deliberate teardown (unmount, or the conversation being
        // deleted) and the caller drops that state, so it stays quiet.
        if (err.name !== 'AbortError' && !settled) {
          settled = true;
          onError && onError(err.message || 'Stream connection failed');
        }
      }
    };

    run();
    return controller;
  },
};

export const fileAPI = {
  upload: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  cleanup: async (namespace) => {
    const response = await api.delete(`/files/cleanup/${encodeURIComponent(namespace)}`);
    return response.data;
  },

  getSupportedTypes: async () => {
    const response = await api.get('/files/supported-types');
    return response.data;
  },
};

export const feedbackAPI = {
  create: async (feedback) => {
    const response = await api.post('/api/feedback', feedback);
    return response.data;
  },
};

// Public Help-form submission. Works whether or not the user is logged in —
// the backend reads the PRISM cookie if present to auto-attach user_id.
// Sends multipart/form-data so an optional image attachment rides along.
export const helpFeedbackAPI = {
  submit: async ({ email, category, frequency, detailsText, imageFile }) => {
    const form = new FormData();
    form.append('email', email);
    form.append('category', category);
    form.append('frequency', frequency);
    form.append('details_text', detailsText);
    if (imageFile) form.append('image', imageFile);

    // axios sets the multipart boundary automatically when Content-Type is
    // not specified explicitly. Skip the JSON default by passing the form
    // directly via fetch — keeps CSRF + cookie handling identical to other
    // multipart endpoints in this file (kb upload).
    const headers = { 'Cache-Control': 'no-cache' };
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;

    const res = await fetch(`${API_URL}/api/help-feedback`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    await guardFetchResponse(res);
    if (!res.ok) {
      let detail = '';
      try {
        const data = await res.json();
        detail = data?.detail || '';
      } catch { /* not json */ }
      const message = detail || `Submission failed (HTTP ${res.status})`;
      const err = new Error(message);
      err.status = res.status;
      // Stable rejection code (see ERROR_CODE_HEADER in the backend) so the
      // modal can show a localized message instead of the English detail.
      err.code = res.headers.get('X-PRISM-Error-Code') || null;
      throw err;
    }
    return res.json();
  },

  // Returns a short-lived signed URL to the PRISM User Manual PDF stored in
  // the private GCS bucket. Public endpoint — the login page must be able to
  // call it without a session cookie. Pass 'en' or 'fr' to select the variant.
  getUserManualUrl: async (language = 'en') => {
    const response = await api.get('/api/help-feedback/user-manual-url', {
      params: { language },
    });
    return response.data?.url || '';
  },
};

export const adminHelpFeedbackAPI = {
  list: async ({ status, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (status) params.status_filter = status;
    const response = await api.get('/api/admin/help-feedback', { params });
    return response.data;
  },

  unreadCount: async () => {
    const response = await api.get('/api/admin/help-feedback/unread-count');
    return response.data;
  },

  get: async (id) => {
    const response = await api.get(`/api/admin/help-feedback/${id}`);
    return response.data;
  },

  // Update the status, the universal comment box, or both. Pass `comment` as
  // an empty string to clear the note; omit it (undefined) to leave it
  // untouched. `status` may be omitted to update the comment only.
  updateStatus: async (id, { status, comment } = {}) => {
    const body = {};
    if (status !== undefined) body.status = status;
    if (comment !== undefined) body.comment = comment;
    const response = await api.patch(
      `/api/admin/help-feedback/${id}/status`,
      body,
    );
    return response.data;
  },

  // Replace the User Manual PDF for the given language ('en' | 'fr').
  // Overwrites the file at the fixed GCS path.
  uploadUserManual: async ({ file, language }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('language', language);

    const headers = { 'Cache-Control': 'no-cache' };
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;

    const res = await fetch(`${API_URL}/api/admin/help-feedback/user-manual`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    await guardFetchResponse(res);
    if (!res.ok) {
      let detail = '';
      try {
        const data = await res.json();
        detail = data?.detail || '';
      } catch { /* not json */ }
      const message = detail || `Upload failed (HTTP ${res.status})`;
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  // Admin-only short-lived signed URL to the Admin Guide PDF (English only).
  getAdminManualUrl: async () => {
    const response = await api.get('/api/admin/help-feedback/admin-manual-url');
    return response.data?.url || '';
  },

  // Replace the Admin Guide PDF. English only — overwrites the fixed GCS path.
  uploadAdminManual: async ({ file }) => {
    const form = new FormData();
    form.append('file', file);

    const headers = { 'Cache-Control': 'no-cache' };
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;

    const res = await fetch(`${API_URL}/api/admin/help-feedback/admin-manual`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    await guardFetchResponse(res);
    if (!res.ok) {
      let detail = '';
      try {
        const data = await res.json();
        detail = data?.detail || '';
      } catch { /* not json */ }
      const message = detail || `Upload failed (HTTP ${res.status})`;
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
};

export const conversationExportAPI = {
  // POST { title, body_html, date_label?, app_title?, banners? } → PDF blob.
  // body_html is the rendered chat transcript that used to go into the print
  // window; the backend sanitizes it again with bleach before WeasyPrint
  // touches it. app_title / banners carry the app's own header strings so the
  // PDF banner matches what the user sees on screen (including language).
  // Triggers a direct download — no print dialog.
  toPdf: async ({ title, body_html, date_label, app_title, banners }) => {
    const response = await api.post(
      '/api/conversations/export/pdf',
      { title, body_html, date_label, app_title, banners },
      { responseType: 'blob' },
    );
    const disposition = response.headers['content-disposition'] || '';
    let filename = 'PRISM_Chat.pdf';
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (match) filename = match[1];

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },
};

export const certificateAPI = {
  // POST { professional_designation, registration_number, period_start,
  // period_end, language } → PDF blob. period_start / period_end are ISO date
  // strings (YYYY-MM-DD) or null. Null on either bound means "open" — backend
  // defaults to first message / now.
  generate: async ({ professional_designation, registration_number, period_start, period_end, language }) => {
    const response = await api.post(
      '/api/certificates/generate',
      { professional_designation, registration_number, period_start, period_end, language },
      { responseType: 'blob' },
    );
    const disposition = response.headers['content-disposition'] || '';
    let filename = `PRISM_CME_Certificate.pdf`;
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (match) filename = match[1];

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },
};

export const adminReportsAPI = {
  // Username autocomplete for the Q&A picker
  searchUsers: async (q) => {
    const response = await api.get('/api/admin/reports/users', { params: { q } });
    return response.data;
  },

  // Download a report as a CSV file. The backend streams text/csv with a
  // Content-Disposition header — we trigger a save by creating an object URL
  // for the blob and clicking a hidden link.
  //
  // We auto-attach the browser's IANA timezone so the CSV gets *_local
  // date/time columns alongside the canonical UTC ones. Caller can override
  // by passing an explicit `tz` in params.
  download: async (kind, params) => {
    let browserTz = 'UTC';
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch { /* very old browsers — fall back to UTC */ }
    const mergedParams = { tz: browserTz, ...(params || {}) };
    const response = await api.get(`/api/admin/reports/${kind}`, {
      params: mergedParams,
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] || '';
    let filename = `prism_${kind}_report.csv`;
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match) filename = match[1];

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },
};

// The six study-type labels the LLM picks from (mirror of STUDY_TYPES in
// backend/app/services/kb_ingestion_service.py). Used by the KB upload card's
// confirmation dropdown.
export const KB_STUDY_TYPES = [
  'THCC-prioritized Systematic review and meta-analyze',
  'THCC-prioritized Randomized Controlled trials',
  'THCC-prioritized Cohort Studies',
  'THCC-prioritized Case series and case reports',
  'THCC-prioritized Expert opinion',
  'THCC-prioritized Unclassified-User Review Required',
];

export const adminKbAPI = {
  // GET the current list of files from the GCS manifest. Backend writes the
  // manifest to a temp file on disk, parses it, then deletes that temp file.
  listFiles: async () => {
    const response = await api.get('/api/admin/kb/files');
    return response.data;
  },

  // DELETE a file end-to-end (GCS PDF, CSV row, manifest entry, vector chunks).
  // The path is URL-encoded so spaces and other safe-filename characters work.
  deleteFile: async (filename) => {
    const response = await api.delete(
      `/api/admin/kb/files/${encodeURIComponent(filename)}`,
    );
    return response.data;
  },

  // Streamed upload. Returns an AbortController so the caller can cancel.
  // Calls onEvent(event) for every parsed SSE frame.
  upload: ({ file, type, citation, canadianGuideline, confirmedType }, callbacks = {}) => {
    const { onEvent, onDone, onError } = callbacks;
    const controller = new AbortController();

    const form = new FormData();
    form.append('file', file);
    form.append('type', type);
    form.append('citation', citation);
    form.append('canadian_guideline', canadianGuideline ? 'true' : 'false');
    if (confirmedType) form.append('confirmed_type', confirmedType);

    // Multipart: do NOT set Content-Type — fetch must set its own with the
    // boundary token. We still need CSRF + cache headers though, so build them
    // by hand instead of using fetchHeaders() (which forces application/json).
    const headers = { 'Cache-Control': 'no-cache' };
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/kb/upload`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: form,
          signal: controller.signal,
        });

        await guardFetchResponse(res);
        if (!res.ok) {
          // Rejections raised before the stream opens (e.g. a filename that
          // cannot be stored) arrive as a normal JSON error body. Surface the
          // `detail` sentence rather than dumping raw JSON at the admin.
          const text = await res.text().catch(() => '');
          let detail = '';
          try { detail = JSON.parse(text)?.detail || ''; } catch { /* not json */ }
          onError && onError(
            typeof detail === 'string' && detail
              ? detail
              : `Server error ${res.status}: ${text}`,
          );
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice('data: '.length).trim();
            if (!jsonStr) continue;
            let event;
            try { event = JSON.parse(jsonStr); } catch { continue; }
            onEvent && onEvent(event);
          }
        }
        onDone && onDone();
      } catch (err) {
        if (err.name !== 'AbortError') {
          onError && onError(err.message || 'Upload stream failed');
        }
      }
    })();

    return controller;
  },
};

export default api;
